import processTemplate from './ProcessTemplate';

/**
 * Renders the landing page template and applies the cleanup passes required before
 * the HTML is accepted by the upload endpoint.
 * @param {string} templateHTML - The landing page template.
 * @param {Object} templateData - The values to substitute into the template.
 * @returns {string} - The processed HTML, ready to upload.
 */
export const buildLandingPageHtml = (templateHTML, templateData) => {
    let processedTemplate = processTemplate(templateHTML, templateData);

    // 1. Fix CSS selectors with # characters
    processedTemplate = processedTemplate.replace(
        /\.svg-filter-#([0-9a-fA-F]{6})/g,
        '.svg-filter-color-$1'
    );

    // 2. Remove HTML comments
    processedTemplate = processedTemplate.replace(/<!--[\s\S]*?-->/g, '');

    // 3. Remove control characters
    processedTemplate = processedTemplate.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');

    return processedTemplate;
};

/**
 * Builds the iframe wrapper stored on the tenancy, pointing at the uploaded page.
 * @param {string} landingPageUrl - The S3 URL of the uploaded landing page.
 * @returns {string} - The iframe HTML.
 */
export const buildLandingPageIframe = (landingPageUrl) => `<p><iframe
  style="position: fixed; top: 0; left: 0; bottom: 0; right: 0; width: 100%; height: 100%; border: none; margin: 0; padding: 0; z-index: 999999; overflow: auto; background-color: transparent; transform: scale(0.95); transform-origin: center center;"
  src="${landingPageUrl}">
  Your browser doesn't support iframes
</iframe></p>`;

/**
 * Renders the landing page, uploads it to S3 and stores the resulting URL on the tenancy.
 * @param {Object} params - The publish parameters.
 * @param {string} params.templateHTML - The landing page template.
 * @param {Object} params.templateData - The values to substitute into the template.
 * @param {Object} params.tenancyData - The current tenancy record.
 * @param {number} params.tenancyId - The tenancy to update.
 * @param {string} [params.tenancyName] - Overrides the name used to build the S3 path.
 * @param {Function} params.uploadLandingPage - The uploadLandingPage RTK Query mutation trigger.
 * @param {Function} params.updateTenancy - The updateTenancy RTK Query mutation trigger.
 * @returns {Promise<string>} - The URL of the uploaded landing page.
 */
export const publishLandingPage = async ({
    templateHTML,
    templateData,
    tenancyData,
    tenancyId,
    tenancyName,
    uploadLandingPage,
    updateTenancy
}) => {
    const processedTemplate = buildLandingPageHtml(templateHTML, templateData);

    // Upload to S3 using RTK Query mutation
    const uploadResult = await uploadLandingPage({
        html: processedTemplate,
        tenancyName: tenancyName || tenancyData?.name
    }).unwrap();

    if (!uploadResult?.success) {
        throw new Error('Failed to upload landing page to S3');
    }

    const landingPageUrl = uploadResult.url;

    // Store the iframe pointing at the uploaded page, plus the URL itself
    const updatedTenancyData = {
        ...tenancyData,
        landingPage: buildLandingPageIframe(landingPageUrl),
        landingPageUrl: landingPageUrl,
        updatedAt: new Date().toISOString()
    };

    const result = await updateTenancy({
        data: updatedTenancyData,
        tenancyId: tenancyId
    }).unwrap();

    if (result && result.message && result.message.includes("error")) {
        throw new Error(`API error: ${result.message}`);
    }

    // Return the uploaded URL rather than relying on the refetched tenancy, since
    // updateTenancy invalidates the Tenancy tag and callers may need the URL immediately.
    return landingPageUrl;
};

export default publishLandingPage;
